/**
 * Shared hook for responsive layout in bulk download UI surfaces.
 *
 * Tracks whether the viewport matches a "mobile" breakpoint and auto-syncs
 * a `filtersExpanded` flag (collapsed on mobile, expanded on desktop).
 * Handles the Safari < 14 legacy MediaQueryList API.
 *
 * Used by:
 *  - StatelessWorkCardsUpsellModal (breakpoint: 567px)
 *  - BulkDownloadPageWorkCards      (breakpoint: 567px)
 */

import { useEffect, useState } from 'react';

export interface UseResponsiveLayoutOptions {
  /** Max-width breakpoint (px) at which the layout is considered "mobile". */
  mobileBreakpoint?: number;
}

export interface UseResponsiveLayoutResult {
  isMobile: boolean;
  filtersExpanded: boolean;
  setFiltersExpanded: (expanded: boolean) => void;
}

const DEFAULT_MOBILE_BREAKPOINT = 567;

export function useResponsiveLayout({
  mobileBreakpoint = DEFAULT_MOBILE_BREAKPOINT,
}: UseResponsiveLayoutOptions = {}): UseResponsiveLayoutResult {
  const query = `(max-width: ${mobileBreakpoint}px)`;

  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches,
  );
  const [filtersExpanded, setFiltersExpanded] = useState(
    () => typeof window === 'undefined' || !window.matchMedia(query).matches,
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(query);
    const sync = () => {
      const mobile = mql.matches;
      setIsMobile(mobile);
      setFiltersExpanded(!mobile);
    };
    sync();

    if ('addEventListener' in mql) {
      mql.addEventListener('change', sync);
      return () => mql.removeEventListener('change', sync);
    }

    // Safari < 14 — legacy MediaQueryList API
    if ('addListener' in mql) {
      // @ts-expect-error deprecated but required for Safari < 14
      mql.addListener(sync);
      return () => {
        // @ts-expect-error deprecated but required for Safari < 14
        mql.removeListener(sync);
      };
    }
  }, [query]);

  return { isMobile, filtersExpanded, setFiltersExpanded };
}

export default useResponsiveLayout;
